import * as React from "react";
import { Text, Select, Flex, Input } from "@theorgchartdev/storybook";
import { createSelectOptions } from "../../../../../../Utils/Misc";
import { CommonSelectProperties } from "./Constants";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { kebabCase } from "@/Utils/formatters";
import { appstr } from "@coreapp/utils/appstr";
import {
  getFieldClassTypeFromGroup,
  removeOrganizedFieldOptionSuffix,
  getOrganizedFieldOptionValue
} from "./Utils";
import FieldTypePill from "./FieldTypePill";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";

export const FormSchemaDataTypes = {
  string: "string",
  int: "int"
};

export function GenericForm({
  formSchema,
  onUpdate,
  initialValues,
  organizedFieldOptions,
  chartConfig,
  errors,
  dataTestIdPrefix
}) {
  // form schema is a list of form input schemas
  // - a form input schema has a propertyName, displayName, dataType, and defaultValue
  // The output is a plain object like {propertyName1: value1, ...}

  // Note: this will need to do a deep copy if we want to support nested properties.

  // TODO: Set these values in the parent for generic form elements
  // remove the redundant recalculation happening here
  const values = React.useMemo(() => {
    let initialValuesCopy = {};
    if (initialValues !== undefined) {
      initialValuesCopy = { ...initialValues };
    }

    for (const formSchemaItem of formSchema) {
      if (initialValuesCopy[formSchemaItem.propertyName] === undefined) {
        initialValuesCopy[formSchemaItem.propertyName] = formSchemaItem.defaultValue;
      }

      if (
        formSchemaItem.isFieldSelector &&
        initialValuesCopy[formSchemaItem.propertyName] &&
        !initialValuesCopy[formSchemaItem.propertyName + "ClassType"] &&
        organizedFieldOptions
      ) {
        const fieldValue = initialValuesCopy[formSchemaItem.propertyName];
        for (const group of organizedFieldOptions) {
          const fieldExists = group.options?.some(option => option.value === fieldValue);
          if (fieldExists) {
            initialValuesCopy[formSchemaItem.propertyName + "ClassType"] =
              getFieldClassTypeFromGroup(group.value);
            break;
          }
        }
      }
    }
    return initialValuesCopy;
  }, [initialValues, formSchema, organizedFieldOptions]);

  // "update" right away so that the mixture of defaults and
  // initial values can be persisted
  React.useEffect(() => {
    onUpdate(values);
  }, []);

  return (
    <Flex gap={"12px"} direction={"column"} width={"100%"}>
      {formSchema.length > 0 ? (
        formSchema.map((formSchemaItem, i) => {
          const onInputUpdate = (v, fieldClassType) => {
            let updatedValues = { ...values };
            updatedValues[formSchemaItem.propertyName] = v;
            if (formSchemaItem.isFieldSelector && fieldClassType) {
              updatedValues[formSchemaItem.propertyName + "ClassType"] = fieldClassType;
            }
            onUpdate(updatedValues);
          };

          const Component = _typeToComponent[formSchemaItem.dataType];
          return (
            <Flex gap={"8px"} direction={"column"} width={"100%"} key={formSchemaItem.propertyName}>
              <Component
                onUpdate={onInputUpdate}
                label={formSchemaItem.displayName}
                initialValue={values[formSchemaItem.propertyName]}
                initialFieldClassType={values[formSchemaItem.propertyName + "ClassType"]}
                options={formSchemaItem.options}
                organizedFieldOptions={organizedFieldOptions}
                chartConfig={chartConfig}
                isFieldSelector={formSchemaItem.isFieldSelector}
                placeholder={formSchemaItem.placeholder}
                require={formSchemaItem.require}
                error={errors?.[formSchemaItem.propertyName]}
                dataTestId={`${dataTestIdPrefix}-${kebabCase(formSchemaItem.propertyName)}`}
              />
            </Flex>
          );
        })
      ) : (
        <Text size={"small"} colorModifier={"gray"}>
          {appstr("Basic math has no configurable settings")}
        </Text>
      )}
    </Flex>
  );
}

// These are all temporary!
const _typeToComponent = {
  string: StringInput,
  integer: StringInput,
  boolean: StringInput,
  select: SelectInput,
  list: EmptyInput,
  choice: EmptyInput
};

function StringInput({ onUpdate, initialValue, label, placeholder, require, error, dataTestId }) {
  // TODO had to use the plain input element for some reason
  return (
    <Input
      dataTestId={dataTestId}
      type={"text"}
      size={"small"}
      value={initialValue}
      label={label}
      placeholder={placeholder}
      require={require}
      message={error}
      status={error ? "error" : null}
      onChange={e => {
        onUpdate(e.target.value);
      }}
    />
  );
}

function EmptyInput() {
  return <div style={{ display: "none" }} />;
}

function SelectInput({
  options,
  onUpdate,
  initialValue,
  initialFieldClassType,
  organizedFieldOptions,
  chartConfig,
  isFieldSelector,
  label,
  placeholder,
  require,
  error,
  dataTestId
}) {
  const selectOptions =
    isFieldSelector && organizedFieldOptions && IsPositionChart(chartConfig)
      ? organizedFieldOptions.map(group => ({
          ...group,
          options: group.options?.map(option => ({
            ...option,
            clickHandler: () => {
              const fieldClassType = getFieldClassTypeFromGroup(group.value);
              const fieldValue = removeOrganizedFieldOptionSuffix(option.value);
              onUpdate(fieldValue, fieldClassType);
            }
          }))
        }))
      : createSelectOptions(options, v => {
          onUpdate(v);
        });

  return (
    <Select
      dataTestId={dataTestId}
      value={
        isFieldSelector && organizedFieldOptions && IsPositionChart(chartConfig)
          ? getOrganizedFieldOptionValue(initialValue, initialFieldClassType)
          : initialValue
      }
      label={label}
      inputOnly={false}
      placeholder={placeholder}
      require={require}
      listProps={FieldSelectListProps()}
      options={selectOptions}
      message={error}
      status={error ? "error" : null}
      rightContent={
        isFieldSelector &&
        organizedFieldOptions &&
        IsPositionChart(chartConfig) &&
        initialValue &&
        initialFieldClassType && <FieldTypePill type={initialFieldClassType} />
      }
      {...CommonSelectProperties}
    />
  );
}
